import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import { Box, Typography, Button, Container, CircularProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import 'slick-carousel/slick/slick.css';
import './HeroCarousel.css';
// slick-theme.css intentionally omitted — it loads a blocking woff icon font.
// This carousel uses PNG custom arrows and inline dots CSS, so no theme styles are needed.
import { CONTAINER_SX } from '../../style/containers';
import styles from './HeroCarousel.module.css';
import { withTrailingSlash } from '../../utils/url';

// Helper to normalize and resolve asset URLs relative to subfolders
const getAssetUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const publicUrl = process.env.PUBLIC_URL || '';
  if (publicUrl && url.startsWith(publicUrl)) {
    return url;
  }
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return `${publicUrl}${normalized}`;
};

// Picks the smallest srcset entry that's >= targetWidth (or the largest entry
// if none qualify) from a "url1 400w, url2 800w, ..." string. Used for CSS
// background-images, which can't take a srcset directly.
const pickVariantUrl = (srcset, targetWidth) => {
  if (!srcset) return null;
  const entries = srcset.split(',').map((entry) => {
    const [url, width] = entry.trim().split(/\s+/);
    return { url, width: parseInt(width, 10) };
  });
  const candidate = entries.find((entry) => entry.width >= targetWidth) || entries[entries.length - 1];
  return candidate ? candidate.url : null;
};

// ─── Custom Arrows ────────────────────────────────────────────────────────────

const ArrowBtn = ({ direction, onClick }) => (
  <Box
    component="button"
    onClick={onClick}
    aria-label={direction === 'left' ? 'Previous slide' : 'Next slide'}
    sx={{
      position: 'absolute',
      [direction]: { xs: 8, md: 20 },
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 3,
      width: { xs: 40, md: 52 },
      height: { xs: 40, md: 52 },
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      p: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0.85,
      transition: 'opacity 0.2s, transform 0.2s',
      '&:hover': { opacity: 1, transform: 'translateY(-50%) scale(1.08)' },
    }}
  >
    <Box
      component="img"
      src={`${process.env.PUBLIC_URL}/assets/images/icons/${direction === 'left' ? 'arrow-left-nav' : 'arrow-right-nav'}.png`}
      alt=""
      aria-hidden="true"
      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
    />
  </Box>
);

// ─── Slide ────────────────────────────────────────────────────────────────────

const ProductImg = ({ src, srcSet, alt, onHide, priority }) => (
  <Box
    component="img"
    src={getAssetUrl(src)}
    srcSet={srcSet || undefined}
    sizes={srcSet ? '(max-width: 480px) 100vw, (max-width: 900px) 36vw, 400px' : undefined}
    alt={alt || ''}
    onError={onHide}
    loading={priority ? 'eager' : 'lazy'}
    fetchpriority={priority ? 'high' : 'low'}
    decoding={priority ? 'sync' : 'async'}
    className={styles.productImgShadow}
    sx={{
      width: '100%',
      maxWidth: { xs: '100%', md: 400 },
      maxHeight: { xs: 220, md: '85%' },
      height: 'auto',
      objectFit: 'contain',
      display: 'block',
    }}
  />
);

const SlideText = ({ slide }) => (
  <Box>
    <Typography
      component="div"
      sx={{
        color: '#fff',
        fontWeight: 700,
        fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.85rem', lg: '2.35rem' },
        lineHeight: 1.2,
        mb: { xs: 1.5, md: 2 },
        maxWidth: { xs: '100%', md: 580, lg: 680, xl: 835 },
      }}
    >
      {slide.title}
    </Typography>
    <Typography
      sx={{
        color: 'rgba(255,255,255,0.85)',
        fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem', lg: '1rem' },
        mb: { xs: 2, md: 3 },
        maxWidth: { xs: '100%', md: 480, lg: 580, xl: 720 },
        lineHeight: 1.6,
      }}
    >
      {slide.subtitle}
    </Typography>
    <Button
      component={RouterLink}
      to={withTrailingSlash(slide.ctaUrl || '#')}
      variant="contained"
      sx={{
        bgcolor: '#178ACB',
        color: '#fff',
        borderRadius: '37px',
        px: '20px',
        py: '10px',
        minWidth: 0,
        fontFamily: 'DM Sans, sans-serif',
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '20px',
        textTransform: 'uppercase',
        letterSpacing: 0,
        boxShadow: 'none',
        outline: '1px solid #fff',
        outlineOffset: '-1px',
        '&:hover': {
          bgcolor: '#fff',
          color: '#178ACB',
          boxShadow: 'none',
          outline: '1px solid #178ACB',
        },
      }}
    >
      {slide.ctaLabel || 'Explore Now'}
    </Button>
  </Box>
);

// CSS background-image can't use srcset, so pick one reasonably-sized variant
// up front instead of always downloading the full-resolution original — a
// static, non-responsive image can't be sized exactly right for every
// viewport, but 1200w covers the common case far better than the source
// upload (often 1920w+) at a fraction of the bytes.
const BG_TARGET_WIDTH = 1200;

const resolveBgSrc = (imageSrcset, image, fallbackImage) =>
  pickVariantUrl(imageSrcset, BG_TARGET_WIDTH) || getAssetUrl(image || fallbackImage || '');

const Slide = ({ slide, isFirst }) => {
  const [showProduct, setShowProduct] = useState(Boolean(slide.productImage));
  // Determine background: prefer primary image, fall back gracefully
  const [bgSrc, setBgSrc] = useState(resolveBgSrc(slide.imageSrcset, slide.image, slide.fallbackImage));

  useEffect(() => {
    setBgSrc(resolveBgSrc(slide.imageSrcset, slide.image, slide.fallbackImage));
    setShowProduct(Boolean(slide.productImage));
  }, [slide.image, slide.imageSrcset, slide.productImage, slide.fallbackImage]);

  // PageSpeed's "LCP request discovery" flags this exact image as missing
  // fetchpriority=high — but it's a CSS background-image (see comment below),
  // and fetchpriority is an HTML attribute that only works on <img>/<link>
  // elements, so it can't be set directly on a CSS property. A <link
  // rel="preload" as="image" fetchpriority="high"> tag is Google's documented
  // workaround for exactly this case — it gets the browser to fetch this
  // specific image at high priority without changing how the slide itself
  // renders. Only the first slide qualifies (it's the only one visible, and
  // therefore the only LCP candidate, before any user interaction).
  useEffect(() => {
    if (!isFirst || !bgSrc) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = bgSrc;
    link.fetchPriority = 'high';
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, [isFirst, bgSrc]);

  // textPosition: 'left' | 'right' | 'center'
  const pos = slide.textPosition || (slide.productImage ? 'right' : 'left');
  const hasProduct = slide.productImage && showProduct;

  return (
    <Box>
      {/*
        Use CSS backgroundImage instead of <img> for the slide background.
        react-slick fade mode sets inactive slides to opacity:0 + display:none,
        which can trigger img onError spuriously (no layout = load failure).
        A CSS background loads independently of element visibility — no flicker.
      */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: 'auto',
          '@media (min-width: 900px)': {
            aspectRatio: '1920 / 600',
          },
          height: { xs: 320, sm: 420, md: 'auto' },
          minHeight: { xs: 320, sm: 420, md: 'auto' },
          // Fixed (not auto) below 480px — every slide's content height differs slightly,
          // so an auto-height box resizes on each slide change. Since the resize and the
          // 600ms fade aren't perfectly synced, the container's own bgcolor flashes through
          // for a frame during navigation. A fixed height tall enough for every slide's
          // content removes that snap entirely.
          '@media (max-width:480px)': { height: 470, minHeight: 470 },
          overflow: 'hidden',
          // CSS background — always loaded, never affected by slick visibility
          bgcolor: '#071b30',
          backgroundImage: bgSrc ? `url(${bgSrc})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >

        {/* Content — px accounts for arrow button width so text is never under arrows */}
        <Container
          maxWidth={false}
          sx={{
            ...CONTAINER_SX,
            position: 'relative',
            zIndex: 2,
            height: '100%',
          }}
        >
          {/* ── layout: product-left / text-right ── */}
          {pos === 'right' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                gap: { xs: 2, md: 4 },
                '@media (max-width:480px)': { flexWrap: 'wrap', height: 470, py: 3 },
              }}
            >
              {/* Product image — left */}
              <Box
                sx={{
                  flex: '0 0 auto',
                  width: { xs: '36%', md: '32%' },
                  '@media (max-width:480px)': { width: '100%', height: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                {hasProduct && (
                  <ProductImg src={slide.productImage} srcSet={slide.productImageSrcset} alt={slide.title} onHide={() => setShowProduct(false)} priority={isFirst} />
                )}
              </Box>
              {/* Text — right */}
              <Box sx={{ flex: 1 }}>
                <SlideText slide={slide} />
              </Box>
            </Box>
          )}

          {/* ── layout: text-left / product-right ── */}
          {pos === 'left' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                gap: { xs: 2, md: 4 },
                '@media (max-width:480px)': { flexWrap: 'wrap', height: 470, py: 3 },
              }}
            >
              {/* Text — left */}
              <Box sx={{ flex: 1 }}>
                <SlideText slide={slide} />
              </Box>
              {/* Product image — right (optional) */}
              {hasProduct && (
                <Box
                  sx={{
                    flex: '0 0 auto',
                    width: { xs: '36%', md: '32%' },
                    '@media (max-width:480px)': { width: '100%', height: 'auto' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <ProductImg src={slide.productImage} srcSet={slide.productImageSrcset} alt={slide.title} onHide={() => setShowProduct(false)} priority={isFirst} />
                </Box>
              )}
            </Box>
          )}

          {/* ── layout: centered text (no product) ── */}
          {pos === 'center' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Box sx={{ textAlign: 'center', maxWidth: 700 }}>
                <SlideText slide={slide} />
              </Box>
            </Box>
          )}
        </Container>
      </Box>
    </Box>
  );
};

// ─── HeroCarousel ─────────────────────────────────────────────────────────────

const HeroCarousel = ({ apiData }) => {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    // Hand off from the static CSS cover (index.html) to this component's own
    // overlay. Removing the style tag here is synchronous with React's first
    // paint so there is zero visible gap between the two dark covers.
    const cover = document.getElementById('hero-loading-cover');
    if (cover) cover.remove();
  }, []);

  useEffect(() => {
    if (!apiData || apiData.length === 0) return;
    setBanners(apiData);
  }, [apiData]);

  const settings = {
    dots: false,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5500,
    pauseOnHover: true,
    fade: true,
    adaptiveHeight: false,
    cssEase: 'ease-in-out',
    arrows: true,
    prevArrow: <ArrowBtn direction="left" />,
    nextArrow: <ArrowBtn direction="right" />,
    responsive: [
      {
        breakpoint: 900,
        settings: {
          adaptiveHeight: true,
        },
      },
    ],
  };

  return (
    <Box
      className="hero-carousel-container"
      sx={{
        bgcolor: '#071b30',
      }}
    >
      {/* Loading overlay — visible until API data has arrived AND slick has initialised. */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 20,
          bgcolor: '#071b30',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: banners.length > 0 ? 0 : 1,
          pointerEvents: banners.length > 0 ? 'none' : 'auto',
          transition: 'opacity 0.4s ease',
        }}
      >
        <CircularProgress size={40} thickness={3} sx={{ color: '#1c8caf' }} />
      </Box>

      <Box
        className="hero-carousel"
        sx={{
          opacity: banners.length > 0 ? 1 : 0,
          visibility: banners.length > 0 ? 'visible' : 'hidden',
          transition: 'opacity 0.4s ease',
        }}
      >
        {banners.length > 0 && (
          <Slider {...settings}>
            {banners.map((slide, idx) => (
              <Slide key={slide.id} slide={slide} isFirst={idx === 0} />
            ))}
          </Slider>
        )}
      </Box>
    </Box>
  );
};

export default HeroCarousel;
